import React, { useState, useRef, useEffect } from 'react';
import { Terminal, Send, ShieldAlert, Key, Download } from 'lucide-react';

const TypewriterText = ({ text, delay = 15 }: { text: string; delay?: number }) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    const interval = setInterval(() => {
      i += 3;
      if (i > text.length) i = text.length;
      setDisplayedText(text.substring(0, i));
      if (i === text.length) {
        clearInterval(interval);
      }
    }, delay);

    return () => clearInterval(interval);
  }, [text, delay]);

  return <>{displayedText}</>;
};

export function Node0Terminal() {
  const [theme, setTheme] = useState<'holographic' | 'legacy'>(() => {
    return (localStorage.getItem('nexorax_node0_theme') as 'holographic' | 'legacy') || 'holographic';
  });
  const [voiceCommand, setVoiceCommand] = useState(false);
  const [searchGrounding, setSearchGrounding] = useState(() => {
    return localStorage.getItem('nexorax_node0_searchGrounding') === 'true';
  });
  const [diagnostic, setDiagnostic] = useState<any>(() => {
    if (typeof window !== 'undefined' && (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__) {
      return (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__;
    }
    return {
      status: 'no active video',
      timestamp: new Date().toLocaleTimeString(),
      videoSrc: 'N/A',
      videoTitle: 'N/A',
      isPlaying: false,
      playerProgress: 0,
      error: null,
      errorMessage: null,
      errorCode: null
    };
  });

  useEffect(() => {
    const handleDiagnostic = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setDiagnostic(customEvent.detail);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('nexorax-video-diagnostic', handleDiagnostic);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('nexorax-video-diagnostic', handleDiagnostic);
      }
    };
  }, []);
  const isHolo = theme === 'holographic';
  const [messages, setMessages] = useState<{ id: string; role: 'user' | 'system'; text: string; timestamp: Date }[]>(() => {
    const savedMsg = localStorage.getItem('nexorax_node0_messages');
    if (savedMsg) {
      try {
        const parsed = JSON.parse(savedMsg);
        return parsed.map((m: any) => ({
          ...m,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
        }));
      } catch (e) {
        console.error("[Node0Terminal] Failed to parse saved messages:", e);
      }
    }
    return [
      { id: '1', role: 'system', text: 'NODE 0 ARCHITECT COMMAND INITIALIZED.\nSECURE REPLICATION LINK: ACTIVE.\nWAITING FOR DIRECTIVE...', timestamp: new Date() }
    ];
  });
  const [input, setInput] = useState(() => {
    return localStorage.getItem('nexorax_node0_input') || '';
  });
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('nexorax_node0_theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('nexorax_node0_searchGrounding', String(searchGrounding));
  }, [searchGrounding]);

  useEffect(() => {
    localStorage.setItem('nexorax_node0_messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('nexorax_node0_input', input);
  }, [input]);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleExport = () => {
    const logData = messages.map(msg => `[${msg.timestamp.toISOString()}] ${msg.role.toUpperCase()}: ${msg.text}`).join('\n\n');
    const blob = new Blob([logData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `NODE0_LOG_${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = { id: Date.now().toString(), role: 'user' as const, text: input.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    
    // Simulate system response
    setTimeout(() => {
      let responseText = `[ARCHITECT LEDGER LOGGED] Execution confirmed for directive: "${input.trim().substring(0, 20)}...". Tiered Extraction Protocol engaged. 1% Charity allocations processed.`;
      
      if (searchGrounding) {
         responseText += `\n[GROUNDING] Competitor cross-reference complete. Zero overlap detected across target sectors.`;
      }
      
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'system', text: responseText, timestamp: new Date() }]);
    }, 1000);

    setInput('');
  };

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window)) return;
    const SpeechRecognition = (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };

    recognition.start();
  };

  useEffect(() => {
    if (voiceCommand) {
       startVoiceInput();
    }
  }, [voiceCommand]);

  return (
    <div className={`flex h-full border rounded-lg overflow-hidden bg-[#050505] transition-all shadow-[0_0_30px_rgba(0,0,0,1)] ${isHolo ? 'border-cyan-500/30 shadow-[0_0_30px_rgba(0,255,255,0.05)]' : 'border-green-500/50 shadow-[0_0_30px_rgba(0,255,0,0.05)]'}`}>
      {/* Sidebar - Optional in dense view, but keeping it visible if enough space */}
      <div className="w-72 border-r border-zinc-800 bg-[#020202] p-4 hidden lg:flex flex-col shrink-0 overflow-y-auto">
        <div className={`flex items-center gap-2 mb-4 ${isHolo ? 'text-cyan-400' : 'text-green-500'}`}>
          <Terminal size={18} />
          <span className="font-mono text-sm tracking-widest font-bold">HISTORY LOG</span>
        </div>
        <div className="h-28 overflow-y-auto space-y-2 pr-2 custom-scrollbar border-b border-zinc-900 pb-3 mb-3">
          {messages.filter(m => m.role === 'user').map(msg => (
            <div key={msg.id} className={`text-[10px] font-mono truncate opacity-70 hover:opacity-100 cursor-pointer transition-opacity ${isHolo ? 'text-slate-400' : 'text-green-600'}`}>
              &gt; {msg.text}
            </div>
          ))}
          {messages.filter(m => m.role === 'user').length === 0 && (
            <span className="text-[10px] text-zinc-650 italic">No instructions logged</span>
          )}
        </div>

        {/* REVOLUTIONARY VIDEO DEPLOY DIAGNOSTIC WIDGET */}
        <div className={`mt-2 pb-4 border-b border-zinc-900 space-y-3`}>
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-cyan-400">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                diagnostic.status === 'playing' ? 'bg-emerald-400' :
                diagnostic.status === 'error' ? 'bg-rose-400' :
                diagnostic.status === 'buffering' ? 'bg-cyan-400' : 'bg-amber-400'
              }`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${
                diagnostic.status === 'playing' ? 'bg-emerald-500' :
                diagnostic.status === 'error' ? 'bg-rose-500' :
                diagnostic.status === 'buffering' ? 'bg-cyan-500' : 'bg-amber-500'
              }`}></span>
            </span>
            <span>VIDEO DIAGNOSTIC HUB</span>
          </div>

          <div className="bg-black/65 border border-zinc-850 p-2.5 rounded-lg font-mono text-[10px] space-y-2">
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-zinc-500 font-extrabold uppercase">STATE:</span>
              <span className={`font-black uppercase tracking-wider rounded px-1.5 py-0.5 text-[9px] ${
                diagnostic.status === 'playing' ? 'bg-emerald-950/20 text-emerald-400 border border-emerald-500/20' :
                diagnostic.status === 'error' ? 'bg-rose-950/30 text-rose-400 border border-rose-500/30 animate-pulse' :
                diagnostic.status === 'buffering' ? 'bg-cyan-950/20 text-cyan-400 border border-cyan-500/20 animate-pulse' : 
                'bg-amber-950/10 text-amber-400 border border-amber-500/10'
              }`}>
                {diagnostic.status}
              </span>
            </div>

            <div className="space-y-0.5">
              <span className="text-zinc-500 block text-[9px]">TARGET TITLE:</span>
              <span className="text-slate-300 block truncate font-sans text-[10px] max-w-[220px]" title={diagnostic.videoTitle}>
                {diagnostic.videoTitle}
              </span>
            </div>

            <div className="space-y-1">
              <span className="text-zinc-500 block text-[9px]">VIDEO STREAM URI:</span>
              {diagnostic.videoSrc !== 'N/A' ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-cyan-500/95 block truncate bg-zinc-950 px-1 py-0.5 rounded border border-white/5 font-mono text-[9px] w-full max-w-[195px] text-left" title={diagnostic.videoSrc}>
                    {diagnostic.videoSrc}
                  </span>
                  <a 
                    href={diagnostic.videoSrc} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="text-indigo-400 hover:text-indigo-300 transition-colors shrink-0 text-xs font-black"
                    title="Open stream URL directly in new tab to diagnose connection / bypass iframe block"
                  >
                    ↗
                  </a>
                </div>
              ) : (
                <span className="text-zinc-600 italic block">No source active</span>
              )}
            </div>

            {diagnostic.status === 'error' && (
              <div className="border border-rose-500/20 bg-rose-500/5 p-2 rounded text-[9px] text-rose-300 space-y-1 text-left leading-normal">
                <span className="font-bold block text-rose-400 text-[10px]">⚠️ MEDIA RENDER FAULT DETECTED:</span>
                <p className="normal-case text-zinc-300 text-[9px] font-sans">{diagnostic.errorMessage || 'Browser blocks video decode or autoplay.'}</p>
                <div className="pt-1 text-zinc-500 uppercase text-[8px] font-sans leading-snug">
                  *Browser has blocked autoplaying. Try interacting directly with the video rendering screen first.
                </div>
              </div>
            )}

            <div className="flex justify-between items-center border-t border-white/5 pt-1.5 text-[8px] text-zinc-600 font-mono">
              <span>SYNC TIME:</span>
              <span>{diagnostic.timestamp}</span>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const testDiagnostic = {
                    status: 'playing',
                    timestamp: new Date().toLocaleTimeString(),
                    videoSrc: "https://assets.mixkit.co/videos/preview/mixkit-rotating-planet-earth-in-outer-space-43034-large.mp4",
                    videoTitle: "Diagnostics Self-test Earth Loop Stream",
                    isPlaying: true,
                    playerProgress: 15,
                    error: null,
                    errorMessage: null,
                    errorCode: null
                  };
                  (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = testDiagnostic;
                  window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: testDiagnostic }));
                  setDiagnostic(testDiagnostic);
                }
              }}
              className="flex-1 text-center py-1 bg-zinc-900 hover:bg-zinc-850 text-slate-300 border border-zinc-800 rounded text-[9px] font-black uppercase transition-all cursor-pointer"
            >
              📊 EMULATE LOOP
            </button>
            
            <button
              onClick={() => {
                const defaultDiag = {
                  status: 'no active video',
                  timestamp: new Date().toLocaleTimeString(),
                  videoSrc: 'N/A',
                  videoTitle: 'N/A',
                  isPlaying: false,
                  playerProgress: 0,
                  error: null,
                  errorMessage: null,
                  errorCode: null
                };
                if (typeof window !== 'undefined') {
                  (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = defaultDiag;
                  window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: defaultDiag }));
                }
                setDiagnostic(defaultDiag);
              }}
              className="py-1 px-1.5 bg-rose-950/25 hover:bg-rose-900/35 border border-rose-500/20 text-rose-450 rounded text-[9px] font-black uppercase transition-all cursor-pointer shrink-0"
              title="Reset Diagnostic Metrics"
            >
              CLR
            </button>
          </div>
        </div>

        <div className={`mt-4 pt-1 flex items-center gap-2 text-[10px] uppercase font-mono ${isHolo ? 'text-indigo-400' : 'text-green-500 opacity-80'}`}>
          <ShieldAlert size={12} />
          <span>CloudSync-02 Active</span>
        </div>
      </div>

      {/* Main Terminal Area */}
      <div className="flex-1 flex flex-col bg-[#020202]">
        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b bg-gradient-to-r from-[#020202] gap-4 sm:gap-0 ${isHolo ? 'border-cyan-500/20 to-slate-900/20' : 'border-green-500/30 to-green-900/10'}`}>
          <div className="flex items-center gap-3">
            <div className="relative flex items-center justify-center w-4 h-4">
              <div className={`absolute inset-0 rounded-full border-2 border-t-transparent animate-spin ${isHolo ? 'border-cyan-500' : 'border-green-500'}`} style={{ animationDuration: isHolo ? '3s' : '0.5s' }}></div>
              <div className={`w-1.5 h-1.5 rounded-full ${isHolo ? 'bg-cyan-400 animate-pulse' : 'bg-green-400'}`}></div>
            </div>
            <h2 className={`font-mono font-bold tracking-widest ${isHolo ? 'text-[#e2e8f0]' : 'text-green-500'}`}>NODE 0: ARCHITECT COMMAND</h2>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono text-slate-500">
            {/* Export Log */}
            <button
              onClick={handleExport}
              title="Export Log Data"
              className={`flex items-center justify-center p-1.5 rounded border border-transparent transition-all ${isHolo ? 'hover:bg-cyan-500/20 hover:border-cyan-500/30 text-cyan-500' : 'hover:bg-green-500/20 hover:border-green-500/30 text-green-500'}`}
            >
              <Download size={14} />
            </button>
            {/* Toggles */}
            <div className={`hidden sm:flex items-center border rounded overflow-hidden text-[9px] ${isHolo ? 'border-indigo-500/30' : 'border-green-500/30'}`}>
               <button 
                  onClick={() => setVoiceCommand(!voiceCommand)}
                  className={`px-2 py-1 font-bold ${voiceCommand ? (isHolo ? 'bg-indigo-500/30 text-indigo-300' : 'bg-green-500/30 text-green-300') : 'text-slate-500 hover:text-white'}`}
               >
                  VOICE API: {voiceCommand ? 'ON' : 'OFF'}
               </button>
               <button 
                  onClick={() => setSearchGrounding(!searchGrounding)}
                  className={`px-2 py-1 font-bold ${searchGrounding ? (isHolo ? 'bg-indigo-500/30 text-indigo-300' : 'bg-green-500/30 text-green-300') : 'text-slate-500 hover:text-white'}`}
               >
                  SEARCH GROUND: {searchGrounding ? 'ON' : 'OFF'}
               </button>
            </div>
            {/* Theme Toggle */}
            <div className="flex items-center border border-white/10 rounded overflow-hidden">
              <button 
                onClick={() => setTheme('holographic')} 
                className={`px-3 py-1 font-bold ${isHolo ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}
              >
                HOLO
              </button>
              <button 
                onClick={() => setTheme('legacy')} 
                className={`px-3 py-1 font-bold ${!isHolo ? 'bg-green-500/20 text-green-500' : 'text-slate-500 hover:text-slate-300'}`}
              >
                LEGACY
              </button>
            </div>
            <div className={`hidden sm:flex items-center gap-2 ${isHolo ? 'text-slate-500' : 'text-green-700'}`}>
              <Key size={12} />
              <span>ROOT_ACCESS</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 font-mono text-sm">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-md whitespace-pre-wrap ${msg.role === 'user' ? (isHolo ? 'bg-cyan-900/20 border border-cyan-500/30 text-cyan-50' : 'bg-green-900/10 border border-green-500/40 text-green-100') : (isHolo ? 'text-slate-300' : 'text-green-500')}`}>
                {msg.role === 'system' && <span className={`font-bold mr-2 ${isHolo ? 'text-indigo-400' : 'text-white/70'}`}>SYS&gt;</span>}
                {msg.role === 'system' ? <TypewriterText text={msg.text} /> : msg.text}
                <div className={`text-[10px] opacity-40 mt-2 text-right ${isHolo ? '' : 'text-green-400'}`}>
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          <div ref={endOfMessagesRef} />
        </div>

        <form onSubmit={handleSend} className={`p-4 border-t bg-[#000000]/60 ${isHolo ? 'border-cyan-500/20' : 'border-green-500/20'}`}>
          <div className="relative flex items-center">
            <span className={`absolute left-4 font-mono font-bold ${isHolo ? 'text-cyan-500' : 'text-green-500'}`}>
              {voiceCommand ? '🎤' : '>'}
            </span>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={voiceCommand ? "Listening..." : "Enter Architect Directive..."}
              className={`w-full bg-transparent border rounded-lg py-3 pl-10 pr-12 focus:outline-none font-mono text-sm text-white transition-colors ${isHolo ? 'border-cyan-500/30 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(0,240,255,0.2)]' : 'border-green-500/40 focus:border-green-400 focus:shadow-[0_0_15px_rgba(0,255,0,0.2)]'}`}
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className={`absolute right-2 p-2 rounded-md disabled:opacity-50 transition-colors ${isHolo ? 'text-cyan-500 hover:bg-cyan-500/10' : 'text-green-500 hover:bg-green-500/10'}`}
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
